import type { AuditEvent } from "./audit.js";
import { createAuditEvent } from "./audit.js";
import type { GameOperation } from "./garage.js";
import type { CorpseDropRequestId, IdGenerator, OperationId, UserId } from "./ids.js";
import type { PlayerIdentity } from "./identity.js";
import { isPlayerDetailsFresh, isPlayerPresenceFresh, type PlayerServerSnapshot } from "./player-snapshot.js";
import { createLedgerEntry, type LedgerEntry } from "./economy.js";

export type CorpseDropRequestStatus = "accepted" | "denied" | "manual_review" | "fulfilled" | "failed";

export interface CorpseDropRuleConfig {
  maxGrowthPercent: number;
  maxHungerPercent: number;
  cooldownSeconds: number;
}

export const defaultCorpseDropRules: CorpseDropRuleConfig = {
  maxGrowthPercent: 60,
  maxHungerPercent: 30,
  cooldownSeconds: 10 * 60
};

export const deinosuchusEmergencyCorpseDropRules: CorpseDropRuleConfig = {
  // Growth is intentionally unrestricted for the automatic Deinosuchus safety net.
  maxGrowthPercent: 100,
  maxHungerPercent: 15,
  cooldownSeconds: 60 * 60
};

export interface DeinosuchusEmergencyCorpse {
  species: string;
  growth: number;
}

const DEINOSUCHUS_EMERGENCY_CORPSE_TIERS = [
  { maxPlayerGrowth: 35, corpseGrowth: 75, species: ["Pachycephalosaurus", "Gallimimus", "Omniraptor"] },
  { maxPlayerGrowth: 70, corpseGrowth: 85, species: ["Carnotaurus", "Ceratosaurus", "Tenontosaurus", "Diabloceratops"] },
  { maxPlayerGrowth: Number.POSITIVE_INFINITY, corpseGrowth: 100, species: ["Tenontosaurus", "Stegosaurus", "Diabloceratops", "Maiasaura"] }
] as const;

export function selectDeinosuchusEmergencyCorpse(
  playerGrowth: number,
  previousSpecies?: string,
  random: () => number = Math.random
): DeinosuchusEmergencyCorpse {
  const normalizedGrowth = Math.max(0, Math.min(100, playerGrowth));
  const tier = DEINOSUCHUS_EMERGENCY_CORPSE_TIERS.find(({ maxPlayerGrowth }) => normalizedGrowth < maxPlayerGrowth)
    ?? DEINOSUCHUS_EMERGENCY_CORPSE_TIERS[DEINOSUCHUS_EMERGENCY_CORPSE_TIERS.length - 1];
  const previous = previousSpecies?.trim().toLowerCase();
  const options = tier.species.filter((species) => species.toLowerCase() !== previous);
  const index = Math.min(options.length - 1, Math.max(0, Math.floor(random() * options.length)));
  return {
    species: options[index] ?? tier.species[0],
    growth: tier.corpseGrowth
  };
}

export type CorpseDropRequestSource = "player_request" | "deinosuchus_emergency";

export type CorpseDropDenialReason =
  | "steam_not_verified"
  | "player_snapshot_missing"
  | "player_offline"
  | "player_snapshot_stale"
  | "growth_unknown"
  | "growth_too_high"
  | "hunger_unknown"
  | "hunger_too_high"
  | "species_not_deinosuchus"
  | "cooldown_active";

export interface CorpseDropEligibility {
  eligible: boolean;
  reasons: CorpseDropDenialReason[];
  nextAvailableAt?: Date;
}

export interface CorpseDropRequest {
  id: CorpseDropRequestId;
  userId: UserId;
  steam64Id: string;
  serverId: string;
  /** Legacy records without this field are player requests. */
  source?: CorpseDropRequestSource;
  playerSnapshotId?: string;
  status: CorpseDropRequestStatus;
  requestedAt: Date;
  cooldownEndsAt?: Date;
  operationId?: OperationId;
  denialReasons: CorpseDropDenialReason[];
  growth?: number;
  hunger?: number;
  catalogItemId?: string;
  purchaseOrderId?: string;
  corpseSpecies?: string;
  corpseGrowth?: number;
  price?: number;
  refundedAt?: Date;
  refundLedgerEntryId?: string;
}

export interface CreateCorpseDropRequestInput {
  identity: PlayerIdentity;
  snapshot?: PlayerServerSnapshot;
  lastAcceptedRequest?: CorpseDropRequest;
  rules: CorpseDropRuleConfig;
  now: Date;
  source?: CorpseDropRequestSource;
  freeCorpse?: {
    species: string;
    growth: number;
  };
  product?: {
    catalogItemId: string;
    purchaseOrderId: string;
    species: string;
    growth: number;
    price: number;
  };
}

export interface CreateCorpseDropRequestResult {
  request: CorpseDropRequest;
  operation?: GameOperation;
  audit: AuditEvent;
  eligibility: CorpseDropEligibility;
}

export function createCorpseDropRefundLedger(
  ids: IdGenerator,
  request: CorpseDropRequest,
  now: Date
): LedgerEntry | undefined {
  if (!request.purchaseOrderId || request.price === undefined || request.price <= 0 || request.refundedAt) {
    return undefined;
  }
  return createLedgerEntry(ids, {
    userId: request.userId,
    type: "credit",
    amount: request.price,
    reason: `Corpse drop refund: ${request.corpseSpecies ?? "spawn failed"}`,
    referenceType: "purchase_order_refund",
    referenceId: request.purchaseOrderId,
    now
  });
}

export function evaluateCorpseDropEligibility(input: CreateCorpseDropRequestInput): CorpseDropEligibility {
  const reasons: CorpseDropDenialReason[] = [];
  const automaticDeinosuchusDrop = input.source === "deinosuchus_emergency";
  const requiresEmergencyFoodRules = !input.product && !automaticDeinosuchusDrop;

  if (input.identity.verificationStatus !== "verified" || !input.identity.steam64Id) {
    reasons.push("steam_not_verified");
  }

  if (!input.snapshot) {
    reasons.push("player_snapshot_missing");
  } else {
    if (!isPlayerPresenceFresh(input.snapshot, input.now)) {
      reasons.push("player_offline");
    }

    if (!isPlayerDetailsFresh(input.snapshot, input.now)) {
      reasons.push("player_snapshot_stale");
    }

    if (automaticDeinosuchusDrop) {
      if (input.snapshot.species?.trim().toLowerCase() !== "deinosuchus") {
        reasons.push("species_not_deinosuchus");
      }
      if (input.snapshot.hunger === undefined) {
        reasons.push("hunger_unknown");
      } else if (input.snapshot.hunger > input.rules.maxHungerPercent) {
        reasons.push("hunger_too_high");
      }
    }

    if (requiresEmergencyFoodRules) {
      if (input.snapshot.growth === undefined) {
        reasons.push("growth_unknown");
      } else if (input.snapshot.growth >= input.rules.maxGrowthPercent) {
        reasons.push("growth_too_high");
      }

      if (input.snapshot.hunger === undefined) {
        reasons.push("hunger_unknown");
      } else if (input.snapshot.hunger >= input.rules.maxHungerPercent) {
        reasons.push("hunger_too_high");
      }
    }
  }

  const source = input.source ?? "player_request";
  const previousSource = input.lastAcceptedRequest?.source ?? "player_request";
  const nextAvailableAt = input.lastAcceptedRequest && previousSource === source
    ? input.lastAcceptedRequest.cooldownEndsAt
    : undefined;
  if (nextAvailableAt && nextAvailableAt.getTime() > input.now.getTime()) {
    reasons.push("cooldown_active");
  }

  return {
    eligible: reasons.length === 0,
    reasons,
    ...(nextAvailableAt ? { nextAvailableAt } : {})
  };
}

export function createCorpseDropRequest(
  ids: IdGenerator,
  input: CreateCorpseDropRequestInput
): CreateCorpseDropRequestResult {
  const source = input.source ?? "player_request";
  const eligibility = evaluateCorpseDropEligibility(input);
  const steam64Id = input.identity.steam64Id ?? input.snapshot?.steam64Id ?? "unknown";
  const serverId = input.snapshot?.serverId ?? "unknown";
  const requestId = ids.next("corpse") as CorpseDropRequestId;
  const operation: GameOperation | undefined = eligibility.eligible
    ? {
        id: ids.next("op") as OperationId,
        type: "corpse_drop.request",
        userId: input.identity.userId,
        targetId: requestId,
        status: "reserved",
        payload: {
          requestId,
          steam64Id,
          serverId,
          requestedAt: input.now.toISOString(),
          source,
          ...(input.snapshot?.id ? { playerSnapshotId: input.snapshot.id } : {}),
          ...(input.snapshot?.eosId ? { eosId: input.snapshot.eosId } : {}),
          ...(input.snapshot?.name ? { playerName: input.snapshot.name } : {}),
          ...(input.snapshot?.species ? { species: input.snapshot.species } : {}),
          ...(input.snapshot?.growth !== undefined ? { growth: input.snapshot.growth } : {}),
          ...(input.snapshot?.hunger !== undefined ? { hunger: input.snapshot.hunger } : {}),
          ...(input.freeCorpse ? {
            corpseSpecies: input.freeCorpse.species,
            corpseGrowth: input.freeCorpse.growth
          } : {}),
          ...(input.product ? {
            catalogItemId: input.product.catalogItemId,
            purchaseOrderId: input.product.purchaseOrderId,
            corpseSpecies: input.product.species,
            corpseGrowth: input.product.growth,
            price: input.product.price
          } : {})
        },
        createdAt: input.now
      }
    : undefined;

  const request: CorpseDropRequest = {
    id: requestId,
    userId: input.identity.userId,
    steam64Id,
    serverId,
    source,
    ...(input.snapshot ? { playerSnapshotId: input.snapshot.id } : {}),
    status: eligibility.eligible ? "accepted" : "denied",
    requestedAt: input.now,
    ...(eligibility.eligible ? { cooldownEndsAt: new Date(input.now.getTime() + input.rules.cooldownSeconds * 1000) } : {}),
    ...(operation ? { operationId: operation.id } : {}),
    denialReasons: eligibility.reasons,
    ...(input.snapshot?.growth !== undefined ? { growth: input.snapshot.growth } : {}),
    ...(input.snapshot?.hunger !== undefined ? { hunger: input.snapshot.hunger } : {}),
    ...(input.freeCorpse ? {
      corpseSpecies: input.freeCorpse.species,
      corpseGrowth: input.freeCorpse.growth
    } : {}),
    ...(input.product ? {
      catalogItemId: input.product.catalogItemId,
      purchaseOrderId: input.product.purchaseOrderId,
      corpseSpecies: input.product.species,
      corpseGrowth: input.product.growth,
      price: input.product.price
    } : {})
  };

  const audit = createAuditEvent(ids, {
    type: source === "deinosuchus_emergency"
      ? eligibility.eligible ? "corpse_drop.deinosuchus_emergency_accepted" : "corpse_drop.deinosuchus_emergency_denied"
      : eligibility.eligible ? "corpse_drop.accepted" : "corpse_drop.denied",
    actorId: source === "deinosuchus_emergency" ? "system" : input.identity.userId,
    targetId: request.id,
    surface: source === "deinosuchus_emergency" ? "worker" : "website",
    summary: source === "deinosuchus_emergency"
      ? eligibility.eligible
        ? "Automatic Deinosuchus emergency food drop reserved at 15% hunger or below."
        : `Automatic Deinosuchus emergency food drop denied: ${eligibility.reasons.join(", ")}.`
      : eligibility.eligible
        ? "Corpse drop request accepted and reserved."
        : `Corpse drop request denied: ${eligibility.reasons.join(", ")}.`,
    metadata: {
      requestId: request.id,
      operationId: operation?.id,
      source,
      denialReasons: eligibility.reasons,
      rules: input.rules,
      product: input.product
    },
    now: input.now
  });

  return {
    request,
    operation,
    audit,
    eligibility
  };
}
export const SUPPORTED_CORPSE_SPECIES = [
  "Allosaurus", "Beipiaosaurus", "Boar", "Boar (baby)", "Bullfrog", "Carnotaurus",
  "Ceratosaurus", "Chicken", "Compsognathus", "Crab", "Deer", "Deer (baby)",
  "Deinosuchus", "Diabloceratops", "Dilophosaurus", "Dryosaurus", "Gallimimus",
  "Goat", "Goat (baby)", "Herrerasaurus", "Hypsilophodon", "Maiasaura", "Omniraptor",
  "Pachycephalosaurus", "Pteranodon", "Rabbit", "Seaturtle", "Stegosaurus",
  "Tenontosaurus", "Triceratops", "Troodon", "Tyrannosaurus"
] as const;

export function isSupportedCorpseSpecies(value: string): boolean {
  const normalized = value.trim().toLowerCase();
  return SUPPORTED_CORPSE_SPECIES.some((species) => species.toLowerCase() === normalized);
}

const dietCorpseSpecies: Record<string, readonly string[]> = {
  allosaurus: ["Stegosaurus", "Tenontosaurus", "Boar", "Diabloceratops", "Triceratops", "Deer", "Maiasaura", "Dryosaurus", "Goat"],
  carnotaurus: ["Pachycephalosaurus", "Tenontosaurus", "Herrerasaurus", "Boar", "Omniraptor", "Diabloceratops", "Troodon", "Deer", "Dryosaurus", "Gallimimus", "Dilophosaurus"],
  ceratosaurus: ["Tenontosaurus", "Pachycephalosaurus", "Ceratosaurus", "Carnotaurus", "Deinosuchus", "Omniraptor", "Diabloceratops", "Deer", "Dilophosaurus", "Stegosaurus", "Beipiaosaurus", "Goat"],
  deinosuchus: ["Tenontosaurus", "Pachycephalosaurus", "Ceratosaurus", "Carnotaurus", "Omniraptor", "Diabloceratops", "Deinosuchus", "Troodon", "Bullfrog", "Gallimimus", "Stegosaurus", "Beipiaosaurus", "Maiasaura", "Seaturtle"],
  dilophosaurus: ["Boar", "Tenontosaurus", "Herrerasaurus", "Ceratosaurus", "Diabloceratops", "Carnotaurus", "Hypsilophodon", "Deer", "Chicken", "Gallimimus", "Maiasaura", "Goat", "Seaturtle", "Dryosaurus"],
  herrerasaurus: ["Crab", "Tenontosaurus", "Pachycephalosaurus", "Boar", "Bullfrog", "Omniraptor", "Hypsilophodon", "Chicken", "Dryosaurus", "Pteranodon", "Beipiaosaurus", "Goat", "Seaturtle", "Gallimimus"],
  omniraptor: ["Boar", "Herrerasaurus", "Pachycephalosaurus", "Ceratosaurus", "Carnotaurus", "Diabloceratops", "Troodon", "Deer", "Rabbit", "Dryosaurus", "Gallimimus", "Stegosaurus"],
  deinonychus: ["Boar", "Herrerasaurus", "Pachycephalosaurus", "Ceratosaurus", "Carnotaurus", "Diabloceratops", "Troodon", "Deer", "Rabbit", "Dryosaurus", "Gallimimus", "Stegosaurus"],
  pteranodon: ["Crab", "Chicken", "Hypsilophodon", "Bullfrog", "Rabbit", "Troodon", "Seaturtle", "Beipiaosaurus"],
  troodon: ["Boar", "Pachycephalosaurus", "Tenontosaurus", "Herrerasaurus", "Chicken", "Rabbit", "Compsognathus", "Hypsilophodon", "Omniraptor", "Goat", "Dryosaurus", "Pteranodon"],
  tyrannosaurus: ["Stegosaurus", "Tenontosaurus", "Pachycephalosaurus", "Boar", "Crab", "Diabloceratops", "Triceratops", "Hypsilophodon", "Deer", "Chicken", "Rabbit", "Maiasaura", "Gallimimus", "Dryosaurus", "Beipiaosaurus", "Goat", "Seaturtle"],
  beipiaosaurus: ["Crab", "Bullfrog"],
  gallimimus: ["Crab", "Bullfrog"]
};

export function selectDietCorpseSpecies(playerSpecies: string, random: () => number = Math.random): string | undefined {
  const normalized = playerSpecies.trim().toLowerCase();
  const options = (dietCorpseSpecies[normalized] ?? []).filter((species) =>
    species.toLowerCase() !== normalized && isSupportedCorpseSpecies(species)
  );
  if (!options.length) return undefined;
  const index = Math.min(options.length - 1, Math.max(0, Math.floor(random() * options.length)));
  return options[index];
}
